import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { supabase } from '../services/supabase'
import { useAuthStore } from './useAuthStore'
import { storage } from '../utils/storage'
import { Platform } from '../utils/platform'

// Get the appropriate storage implementation
const getStorage = () => {
  if (Platform.isNative && (global as any).__FLOWS_STORAGE__) {
    return (global as any).__FLOWS_STORAGE__;
  }
  return storage;
};

export interface NotificationSettings {
  // Transaction notifications
  transactionCreated: boolean
  transactionDeleted: boolean
  largeTransactions: boolean
  recurringTransactions: boolean
  categoryChanged: boolean
  transferComplete: boolean

  // System notifications
  exchangeRateUpdates: boolean
  newFeatures: boolean
  appUpdates: boolean
  securityAlerts: boolean
  syncStatus: boolean

  // Budget notifications
  budgetApproaching: boolean
  budgetExceeded: boolean
  budgetReset: boolean

  // Display settings
  showInApp: boolean
  pushNotifications: boolean
  soundEnabled: boolean
  vibrationEnabled: boolean
}

export const defaultSettings: NotificationSettings = {
  transactionCreated: true,
  transactionDeleted: true,
  largeTransactions: true,
  recurringTransactions: true,
  categoryChanged: true,
  transferComplete: true,
  exchangeRateUpdates: true,
  newFeatures: true,
  appUpdates: true,
  securityAlerts: true,
  syncStatus: true,
  budgetApproaching: true,
  budgetExceeded: true,
  budgetReset: true,
  showInApp: true,
  pushNotifications: true,
  soundEnabled: true,
  vibrationEnabled: true,
}

interface NotificationSettingsStore {
  settings: NotificationSettings
  isLoading: boolean
  error: string | null
  updateSettings: (newSettings: Partial<NotificationSettings>) => Promise<void>
  resetSettings: () => Promise<void>
  loadSettings: () => Promise<void>
}

export const useNotificationSettingsStore = create<NotificationSettingsStore>()(
  persist(
    (set, get) => ({
      settings: defaultSettings,
      isLoading: false,
      error: null,

      loadSettings: async () => {
        const user = useAuthStore.getState().user
        if (!user?.id) {
          set({ error: 'User not authenticated' })
          return
        }

        try {
          set({ isLoading: true, error: null })
          
          const { data, error } = await supabase
            .from('notification_settings')
            .select('*')
            .eq('id', user.id)
            .single()

          if (error) {
            // If no settings found, create default settings
            if (error.code === 'PGRST116') { // PGRST116 is "no rows returned"
              // Only update state if settings are different
              const currentSettings = get().settings
              const settingsChanged = JSON.stringify(currentSettings) !== JSON.stringify(defaultSettings)
              
              if (settingsChanged) {
                set({ settings: defaultSettings })
              }
              
              // Create default settings in database
              const { error: insertError } = await supabase
                .from('notification_settings')
                .insert([{
                  id: user.id,
                  transaction_created: defaultSettings.transactionCreated,
                  transaction_deleted: defaultSettings.transactionDeleted,
                  large_transactions: defaultSettings.largeTransactions,
                  recurring_transactions: defaultSettings.recurringTransactions,
                  category_changed: defaultSettings.categoryChanged,
                  transfer_complete: defaultSettings.transferComplete,
                  exchange_rate_updates: defaultSettings.exchangeRateUpdates,
                  new_features: defaultSettings.newFeatures,
                  app_updates: defaultSettings.appUpdates,
                  security_alerts: defaultSettings.securityAlerts,
                  sync_status: defaultSettings.syncStatus,
                  budget_approaching: defaultSettings.budgetApproaching,
                  budget_exceeded: defaultSettings.budgetExceeded,
                  budget_reset: defaultSettings.budgetReset,
                  show_in_app: defaultSettings.showInApp,
                  push_notifications: defaultSettings.pushNotifications,
                  sound_enabled: defaultSettings.soundEnabled,
                  vibration_enabled: defaultSettings.vibrationEnabled,
                  updated_at: new Date().toISOString()
                }])
              
              if (insertError) throw insertError
            } else {
              throw error
            }
          } else {
            // Map database fields to our settings object
            const mappedSettings: NotificationSettings = {
              transactionCreated: data.transaction_created ?? defaultSettings.transactionCreated,
              transactionDeleted: data.transaction_deleted ?? defaultSettings.transactionDeleted,
              largeTransactions: data.large_transactions ?? defaultSettings.largeTransactions,
              recurringTransactions: data.recurring_transactions ?? defaultSettings.recurringTransactions,
              categoryChanged: data.category_changed ?? defaultSettings.categoryChanged,
              transferComplete: data.transfer_complete ?? defaultSettings.transferComplete,
              exchangeRateUpdates: data.exchange_rate_updates ?? defaultSettings.exchangeRateUpdates,
              newFeatures: data.new_features ?? defaultSettings.newFeatures,
              appUpdates: data.app_updates ?? defaultSettings.appUpdates,
              securityAlerts: data.security_alerts ?? defaultSettings.securityAlerts,
              syncStatus: data.sync_status ?? defaultSettings.syncStatus,
              budgetApproaching: data.budget_approaching ?? defaultSettings.budgetApproaching,
              budgetExceeded: data.budget_exceeded ?? defaultSettings.budgetExceeded,
              budgetReset: data.budget_reset ?? defaultSettings.budgetReset,
              showInApp: data.show_in_app ?? defaultSettings.showInApp,
              pushNotifications: data.push_notifications ?? defaultSettings.pushNotifications,
              soundEnabled: data.sound_enabled ?? defaultSettings.soundEnabled,
              vibrationEnabled: data.vibration_enabled ?? defaultSettings.vibrationEnabled,
            }
            
            // Only update state if settings are different
            const currentSettings = get().settings
            const settingsChanged = JSON.stringify(currentSettings) !== JSON.stringify(mappedSettings)
            
            if (settingsChanged) {
              set({ settings: mappedSettings })
            }
          }
        } catch (error) {
          console.error('Error loading notification settings:', error)
          set({ error: error instanceof Error ? error.message : 'Failed to load notification settings' })
        } finally {
          set({ isLoading: false })
        }
      },

      updateSettings: async (newSettings: Partial<NotificationSettings>) => {
        const user = useAuthStore.getState().user
        if (!user?.id) {
          set({ error: 'User not authenticated' })
          return
        }

        try {
          set({ isLoading: true, error: null })
          const updatedSettings = { ...get().settings, ...newSettings }
          
          // Update local state immediately for optimistic updates
          set({ settings: updatedSettings })

          // Map our settings object to database fields
          const dbSettings = {
            id: user.id,
            transaction_created: updatedSettings.transactionCreated,
            transaction_deleted: updatedSettings.transactionDeleted,
            large_transactions: updatedSettings.largeTransactions,
            recurring_transactions: updatedSettings.recurringTransactions,
            category_changed: updatedSettings.categoryChanged,
            transfer_complete: updatedSettings.transferComplete,
            exchange_rate_updates: updatedSettings.exchangeRateUpdates,
            new_features: updatedSettings.newFeatures,
            app_updates: updatedSettings.appUpdates,
            security_alerts: updatedSettings.securityAlerts,
            sync_status: updatedSettings.syncStatus,
            budget_approaching: updatedSettings.budgetApproaching,
            budget_exceeded: updatedSettings.budgetExceeded,
            budget_reset: updatedSettings.budgetReset,
            show_in_app: updatedSettings.showInApp,
            push_notifications: updatedSettings.pushNotifications,
            sound_enabled: updatedSettings.soundEnabled,
            vibration_enabled: updatedSettings.vibrationEnabled,
            updated_at: new Date().toISOString()
          }

          // Update Supabase
          const { error } = await supabase
            .from('notification_settings')
            .upsert(dbSettings)

          if (error) throw error
        } catch (error) {
          console.error('Error updating notification settings:', error)
          // Revert to previous settings on error
          set({ 
            settings: get().settings,
            error: error instanceof Error ? error.message : 'Failed to update notification settings'
          })
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      resetSettings: async () => {
        const user = useAuthStore.getState().user
        if (!user?.id) {
          set({ error: 'User not authenticated' })
          return
        }

        try {
          set({ isLoading: true, error: null })
          
          // Update local state immediately
          set({ settings: defaultSettings })

          // Map default settings to database fields
          const dbSettings = {
            id: user.id,
            transaction_created: defaultSettings.transactionCreated,
            transaction_deleted: defaultSettings.transactionDeleted,
            large_transactions: defaultSettings.largeTransactions,
            recurring_transactions: defaultSettings.recurringTransactions,
            category_changed: defaultSettings.categoryChanged,
            transfer_complete: defaultSettings.transferComplete,
            exchange_rate_updates: defaultSettings.exchangeRateUpdates,
            new_features: defaultSettings.newFeatures,
            app_updates: defaultSettings.appUpdates,
            security_alerts: defaultSettings.securityAlerts,
            sync_status: defaultSettings.syncStatus,
            budget_approaching: defaultSettings.budgetApproaching,
            budget_exceeded: defaultSettings.budgetExceeded,
            budget_reset: defaultSettings.budgetReset,
            show_in_app: defaultSettings.showInApp,
            push_notifications: defaultSettings.pushNotifications,
            sound_enabled: defaultSettings.soundEnabled,
            vibration_enabled: defaultSettings.vibrationEnabled,
            updated_at: new Date().toISOString()
          }

          // Update Supabase
          const { error } = await supabase
            .from('notification_settings')
            .upsert(dbSettings)

          if (error) throw error
        } catch (error) {
          console.error('Error resetting notification settings:', error)
          set({ error: error instanceof Error ? error.message : 'Failed to reset notification settings' })
          throw error
        } finally {
          set({ isLoading: false })
        }
      },
    }),
    {
      name: 'notification-settings-store',
      storage: createJSONStorage(() => getStorage()),
      partialize: (state) => ({ settings: state.settings }),
    }
  )
)
